import React, { useState } from 'react';
import { Album, X, Globe, Phone, Image } from 'lucide-react';
import { RetailerPhoto } from '../../../types/retailer';

interface RetailerDetailsProps {
    selectedRetailer: any;
}

const RetailerDetails: React.FC<RetailerDetailsProps> = ({ selectedRetailer }) => {
    const [showModal, setShowModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setShowModal(true);
    };

    return (
        <div>
            {/* Contact Information Section */}
            <div className='p-4'>
                {(selectedRetailer?.website || selectedRetailer?.phone_number) && (
                    <>
                        <label className="block text-left text-sm font-medium text-white opacity-70 mb-2">
                            Contact Information
                        </label>
                        
                        {selectedRetailer?.website && (
                            <a 
                                href={selectedRetailer.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className='flex items-center bg-primary-blue-90 p-4 rounded-2xl mb-3 hover:bg-primary-blue-80 transition-colors'
                            >
                                <Globe className='w-5 h-5 mr-3 text-secondary-gray' />
                                <span className="!text-white truncate">{selectedRetailer.website}</span>
                            </a>
                        )}

                        {selectedRetailer?.phone_number && (
                            <a 
                                href={`tel:${selectedRetailer.phone_number}`}
                                className='flex items-center bg-primary-blue-90 p-4 rounded-2xl mb-3 hover:bg-primary-blue-80 transition-colors'
                            >
                                <Phone className='w-5 h-5 mr-3 text-secondary-gray' />
                                <span className="!text-white">{selectedRetailer.phone_number}</span>
                            </a>
                        )}
                    </>
                )}
            </div>

            {/* Photos Section */}
            <div className='flex flex-col mt-4 p-4'>
                <div className='flex items-center justify-between'>
                    <h2 className='text-md font-semibold text-left text-gray-light'>
                        Gallery Photos
                    </h2>
                    <div className='flex items-center space-x-2'>
                        <span className='text-md font-semibold text-gray-light'>
                            {selectedRetailer?.photos?.length ?? 0}
                        </span>
                        <Album className='w-4 h-4 text-gray-light' />
                    </div>
                </div>
                {selectedRetailer?.photos?.length > 0 ? (
                    <div className='grid grid-cols-3 gap-2 mt-4'>
                        {selectedRetailer?.photos.map((photo: RetailerPhoto, index: number) => (
                            <div key={photo.id} className='relative group'>
                                <img
                                    src={photo.s3_url}
                                    className='w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity'
                                    alt={photo.caption || `Retailer photo ${index + 1}`}
                                    onClick={() => handleImageClick(index)}
                                />
                                {photo.caption && (
                                    <div className='absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate'>
                                        {photo.caption}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className='text-center text-gray-400 bg-primary-blue-80 py-4 px-4 rounded-lg mt-4 flex items-center justify-center gap-2'>
                        <Image className='w-5 h-5' />
                        No photos available
                    </p>
                )}
            </div>

            {/* Image Modal */}
            {showModal && selectedRetailer?.photos?.[currentImageIndex] && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" 
                    onClick={() => setShowModal(false)}
                >
                    <div className="relative max-w-5xl max-h-[90vh]">
                        <button
                            className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowModal(false);
                            }}
                        >
                            <X size={24} />
                        </button>
                        <img
                            src={selectedRetailer.photos[currentImageIndex].s3_url}
                            alt={selectedRetailer.photos[currentImageIndex].caption || 'Retailer photo'}
                            className="max-h-[85vh] max-w-full object-contain rounded-lg"
                        />
                        {selectedRetailer.photos[currentImageIndex].caption && (
                            <div className='absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-4 rounded-b-lg'>
                                {selectedRetailer.photos[currentImageIndex].caption}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RetailerDetails;

